/**
 * Gems Mine — the game page. PHASE 1: standalone, practice wallet, honest
 * client randomness; every money seam marked for the phase-2 server swap.
 *
 * FLOW = the Dock Wizard spec (docs/GEMS_MINE_PLAN.md + judged redesign):
 * the bottom third is ONE transforming machine (bet card → cash lever →
 * rebet deck); a Status Strip always says the one thing to do next; an ember
 * frame + wall colour-grade make LIVE readable from across the room.
 * Engineer's law: wall warm+bright = you can dig; cold+dim = you cannot;
 * ember frame on = money in play. Gold button = start money, green = take
 * money — that is the entire grammar.
 *
 * All shown multipliers are TOTAL RETURN (platform X-doctrine, fmtX → X2.4).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DANGERS, type Danger, type GemKind, TILES,
  ladder, cashoutReturn, rungMultiplier, placeBombs, gemFor, fmtX,
} from './engine';
import {
  ArtDefs, Gem, Bomb, Pickaxe, TileFace, Socket, CrackBurst, Shards,
  RaysBurst, Explosion, Lantern, TimberBeam, TimberProp, VeinOverlay,
} from './Art';
import {
  initGmSound, isGmSoundOn, setGmSoundOn, unlockGmSound,
  sStrike, sGem, sRung, sBoom, sCashout, sTap, sDenied, sRoundStart, sStreak,
  initGmMusic, isGmMusicOn, setGmMusicOn, startGmMusic, stopGmMusic,
  duckGmMusic, bindGmMusicVisibility,
} from './sound';
import { tickerBatch } from './fakes';
import './gems.css';

/* press(): the roulette tap contract — pointerdown arms, pointerup on the
   same element fires. No onClick anywhere. */
const armed = new WeakMap<Element, number>();
function press(fn: () => void) {
  return {
    onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); armed.set(e.currentTarget, e.pointerId); },
    onPointerUp: (e: React.PointerEvent) => {
      e.stopPropagation();
      if (armed.get(e.currentTarget) !== e.pointerId) return;
      armed.delete(e.currentTarget);
      if ((e.currentTarget as HTMLButtonElement).disabled) return;
      fn();
    },
    onPointerCancel: (e: React.PointerEvent) => { armed.delete(e.currentTarget); },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (e.repeat) return;
      e.preventDefault(); e.stopPropagation();
      if ((e.currentTarget as HTMLButtonElement).disabled) return;
      fn();
    },
  };
}

/* PHASE-2 SEAM: becomes GET /gems_mine/state's wallet. */
const PRACTICE_START = 10000;
const STEPS = [10, 50, 100, 500, 1000, 5000];
/* THE chip. Same object as roulette's tray token (platform doctrine: every
   coin on every table is the one casino chip players already know). */
const CHIPS = STEPS;

/* risk segments speak SAFE/BOLD/WILD — bomb-icon count carries the meaning;
   never a numeral with × on a risk control (the × glyph belongs to returns) */
const RISK_LABEL: Record<Danger, string> = { careful: 'SAFE', bold: 'BOLD', wild: 'WILD' };
const RISK_ORDER: Danger[] = ['careful', 'bold', 'wild'];

type TileState = 'covered' | 'striking' | 'gem' | 'bomb' | 'ghost';
type Phase = 'set' | 'arming' | 'live' | 'winFlash' | 'bustFlash' | 'settled';

const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

/* SHIFT counter: the lobby feel with zero latency. Per-device, daily IST,
   seeded 20–60 so it never reads SHIFT 1. */
function loadShift(): number {
  const today = new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
  try {
    const raw = lsGet('gm_shift');
    if (raw) { const v = JSON.parse(raw); if (v.d === today && typeof v.n === 'number') return v.n; }
  } catch { /* fall through */ }
  const n = 20 + Math.floor(Math.random() * 41);
  lsSet('gm_shift', JSON.stringify({ d: today, n }));
  return n;
}
function bumpShift(n: number) {
  const today = new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
  lsSet('gm_shift', JSON.stringify({ d: today, n }));
  return n;
}

function loadLastBet(): { stake: number; danger: Danger } {
  try {
    const raw = lsGet('gm_lastBet');
    if (raw) {
      const v = JSON.parse(raw);
      if (STEPS.includes(v.stake) && RISK_ORDER.includes(v.danger)) return v;
    }
  } catch { /* default */ }
  return { stake: 100, danger: 'bold' };
}

const GmPage = () => {
  const last = useMemo(loadLastBet, []);
  const [balance, setBalance] = useState(PRACTICE_START);
  const [phase, setPhase] = useState<Phase>('set');
  const [danger, setDanger] = useState<Danger>(last.danger);
  const [stake, setStake] = useState(last.stake);

  const [tiles, setTiles] = useState<TileState[]>(() => Array(TILES).fill('covered'));
  const [picks, setPicks] = useState(0);
  const [result, setResult] = useState<{ won: boolean; amount: number; rung: number; auto?: boolean } | null>(null);
  const [soundOn, setSoundOn] = useState(initGmSound);
  const [musicOn, setMusicOn] = useState(initGmMusic);
  const [shake, setShake] = useState(false);
  const [shift, setShift] = useState(loadShift);
  const [auto, setAuto] = useState(() => lsGet('gm_auto') === '1');
  const [autoWait, setAutoWait] = useState(false);       // KEEP GOING? interstitial
  const [draining, setDraining] = useState(false);       // AUTO drain ring running
  const [stamp, setStamp] = useState<{ text: string; k: number } | null>(null);
  const [hint, setHint] = useState(false);               // TAP A ROCK for new miners
  const [stripFx, setStripFx] = useState<string | null>(null); // amber flash message
  const [walletFx, setWalletFx] = useState<'' | 'dip' | 'flash'>('');

  /* the round's hidden truth. PHASE-2 SEAM: deleted — the server answers each
     strike (thimbles pattern: no committed layout exists to leak). */
  const bombsRef = useRef<Set<number>>(new Set());
  const seedRef = useRef(1);
  /* money-boundary guards live in refs — a guard a timer consults must never
     be render state (the roulette bettingRef lesson) */
  const liveRef = useRef(false);
  const armingRef = useRef(false);
  const strikingRef = useRef(false);
  const queuedRef = useRef<number | null>(null);         // exactly ONE tap may queue mid-swing
  const settledAtRef = useRef(0);                        // 350ms rage-tap guard
  const flashAtRef = useRef(0);                          // 700ms tap-to-skip gate
  const autoRunsRef = useRef(0);
  const roundBombsRef = useRef(3);                       // bombs of the RUNNING round
  const roundStakeRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => {
    timersRef.current.push(window.setTimeout(fn, ms));
  }, []);
  useEffect(() => () => { timersRef.current.forEach(window.clearTimeout); }, []);

  const bombs = DANGERS[danger].bombs;
  const rungs = useMemo(() => ladder(bombs), [bombs]);
  const liveBombs = roundBombsRef.current;
  const cashAmt = cashoutReturn(roundStakeRef.current, liveBombs, picks);
  const nowX = rungMultiplier(liveBombs, picks);
  const nextX = rungMultiplier(liveBombs, picks + 1);

  /* pure-updater discipline: refs mirror state for synchronous game logic */
  const tilesRef = useRef(tiles);
  useEffect(() => { tilesRef.current = tiles; }, [tiles]);
  const picksRef = useRef(0);
  useEffect(() => { picksRef.current = picks; }, [picks]);

  /* --gm-w/--gm-h must be TRUE: every size in gems.css scales off them */
  const appRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = appRef.current; if (!el) return;
    const ro = new ResizeObserver(() => {
      el.style.setProperty('--gm-w', `${el.clientWidth}px`);
      el.style.setProperty('--gm-h', `${el.clientHeight}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* music: mount → play; leave → silence. */
  useEffect(() => {
    if (isGmMusicOn() && isGmSoundOn()) startGmMusic();
    const unbind = bindGmMusicVisibility();
    return () => { unbind(); stopGmMusic(); };
  }, []);

  /* ambient dust — layout-stable random once */
  const motes = useMemo(() => Array.from({ length: 9 }, (_, i) => ({
    left: `${6 + (i * 37) % 88}%`, top: `${12 + (i * 53) % 60}%`,
    size: 3 + (i * 7) % 6,
    t: `${11 + (i * 5) % 9}s`, d: `${(i * 1.7) % 8}s`,
    dx: `${((i * 13) % 50) - 25}px`, dy: `${-60 - (i * 23) % 90}px`,
  })), []);
  const ticker = useMemo(() => tickerBatch(9), []);

  const flashStrip = useCallback((m: string) => {
    setStripFx(m);
    later(() => setStripFx(null), 1200);
  }, [later]);

  /* ── SETTLED plumbing ─────────────────────────────────────────────── */
  const phaseRef = useRef<Phase>('set');
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const idleTRef = useRef(0);
  const toSettled = useCallback(() => {
    /* both the 1600ms timer and a tap-skip race here — first one wins */
    if (phaseRef.current !== 'winFlash' && phaseRef.current !== 'bustFlash') return;
    phaseRef.current = 'settled';
    setPhase('settled');
    settledAtRef.current = performance.now();
    /* 45s idle → the dock quietly morphs back to the bet card */
    window.clearTimeout(idleTRef.current);
    idleTRef.current = window.setTimeout(() => {
      setPhase((p) => (p === 'settled' ? 'set' : p));
    }, 45_000);
  }, []);
  useEffect(() => () => window.clearTimeout(idleTRef.current), []);

  /* ── ARMING: the six-channel start moment ─────────────────────────── */
  const arm = useCallback((stakeOverride?: number) => {
    unlockGmSound();
    const bet = stakeOverride ?? stake;
    if (armingRef.current || liveRef.current) return;
    if (bet > balance) { flashStrip('NOT ENOUGH BALANCE'); sDenied(); return; }
    armingRef.current = true;
    window.clearTimeout(idleTRef.current);
    /* PHASE-2 SEAM: POST /gems_mine/play {stake, danger} fires HERE (t=0);
       ARMING stretches to the server ack, >2500ms refunds + strip D11. */
    setBalance((b) => b - bet);
    setWalletFx('dip'); later(() => setWalletFx(''), 700);
    roundStakeRef.current = bet;
    roundBombsRef.current = DANGERS[danger].bombs;
    lsSet('gm_lastBet', JSON.stringify({ stake: bet, danger }));
    setShift((n) => bumpShift(n + 1));
    setPicks(0); picksRef.current = 0;
    setResult(null);
    setStamp(null);
    setDraining(false);
    seedRef.current = (Math.random() * 0xffffffff) >>> 0;
    bombsRef.current = placeBombs(DANGERS[danger].bombs, Math.random);
    setTiles(Array(TILES).fill('covered'));
    setPhase('arming');
    sRoundStart();
    navigator.vibrate?.([20, 40, 20]);
    later(() => {
      armingRef.current = false;
      liveRef.current = true;
      setPhase('live');
      /* new miners get a pointing hand if they hesitate */
      const rounds = Number(lsGet('gm_rounds') || 0) + 1;
      lsSet('gm_rounds', String(rounds));
      if (rounds <= 3) later(() => { if (liveRef.current && picksRef.current === 0) setHint(true); }, 2500);
    }, 600);
  }, [stake, balance, danger, flashStrip, later]);

  /* ── CASH OUT (the green half of the grammar) ─────────────────────── */
  const cashout = useCallback((autoClear = false) => {
    if (!liveRef.current) return;
    const k = picksRef.current;
    liveRef.current = false;
    /* PHASE-2 SEAM: POST /gems_mine/cashout → server credit + newBalance. */
    const amt = cashoutReturn(roundStakeRef.current, bombsRef.current.size, k);
    setBalance((b) => b + amt);
    setWalletFx('flash'); later(() => setWalletFx(''), 900);
    duckGmMusic(1600);
    sCashout(k);
    navigator.vibrate?.([20, 30, 20, 30, 60]);
    setTiles((p) => p.map((t) => (t === 'covered' ? 'ghost' : t)));
    setResult({ won: true, amount: amt, rung: k, auto: autoClear });
    setPhase('winFlash');
    flashAtRef.current = performance.now();
    later(toSettled, 1600);
  }, [later, toSettled]);

  /* ── STRIKE ───────────────────────────────────────────────────────── */
  const strike = useCallback(function strikeFn(i: number) {
    if (!liveRef.current) return;
    if (strikingRef.current) { queuedRef.current ??= i; return; }  // one tap queues
    if (tilesRef.current[i] !== 'covered') return;
    strikingRef.current = true;
    setHint(false);
    const isBomb = bombsRef.current.has(i);   // PHASE-2 SEAM: await POST /pick
    sStrike();
    navigator.vibrate?.(12);
    setTiles((prev) => prev.map((t, k) => (k === i ? 'striking' : t)));
    later(() => {
      strikingRef.current = false;
      if (!liveRef.current) { queuedRef.current = null; return; }
      if (isBomb) {
        liveRef.current = false;
        queuedRef.current = null;
        setShake(true);
        later(() => setShake(false), 500);
        duckGmMusic(1400);
        sBoom();
        navigator.vibrate?.([80, 50, 120]);
        setTiles((p) => p.map((t, k) =>
          k === i ? 'bomb' : (t === 'covered' ? 'ghost' : t)));
        setResult({ won: false, amount: 0, rung: 0 });
        setPhase('bustFlash');
        flashAtRef.current = performance.now();
        later(toSettled, 1600);
      } else {
        const nk = picksRef.current + 1;
        picksRef.current = nk;
        sGem(gemFor(seedRef.current, i), nk);
        sRung(nk);
        setPicks(nk);
        setTiles((p) => p.map((t, k) => (k === i ? 'gem' : t)));
        navigator.vibrate?.(8);
        /* streak stamps — praise in the corner, never over the slabs */
        const level = nk === 3 ? 0 : nk === 5 ? 1 : nk === 8 ? 2 : -1;
        if (level >= 0) {
          setStamp({ text: ['HOT!', 'ON FIRE!', 'LEGEND!'][level], k: nk });
          sStreak(level);
          later(() => setStamp((s) => (s?.k === nk ? null : s)), 1400);
        }
        if (nk >= TILES - bombsRef.current.size) {
          later(() => cashout(true), 650);                 // cleared the face
        } else {
          const q = queuedRef.current; queuedRef.current = null;
          if (q != null) strikeFn(q);
        }
      }
    }, 300);
  }, [cashout, later, toSettled]);

  /* ── REBET / CHANGE BET ───────────────────────────────────────────── */
  const rebet = useCallback(() => {
    if (performance.now() - settledAtRef.current < 350) return;  // rage-tap guard
    if (autoWait) { setAutoWait(false); autoRunsRef.current = 0; }
    setDraining(false);
    if (stake > balance) {
      const fit = [...STEPS].reverse().find((s) => s <= balance);
      if (!fit) { flashStrip('BALANCE IS LOW'); sDenied(); return; }
      setStake(fit);
      arm(fit);
      return;
    }
    arm();
  }, [arm, autoWait, stake, balance, flashStrip]);

  const changeBet = useCallback(() => { setDraining(false); setPhase('set'); sTap(); }, []);

  /* AUTO: every SETTLED self-fires REBET after a visible 1400ms drain; any
     dock tap cancels that round; every 10 rounds asks KEEP GOING? */
  useEffect(() => {
    if (phase !== 'settled' || !auto) return;
    if (stake > balance && ![...STEPS].some((s) => s <= balance)) return;
    autoRunsRef.current += 1;
    if (autoRunsRef.current % 10 === 0) { setAutoWait(true); return; }
    setDraining(true);
    const t = window.setTimeout(() => { setDraining(false); rebet(); }, 1400);
    return () => { window.clearTimeout(t); setDraining(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, auto]);

  /* flashes are tap-skippable after 700ms */
  const skipFlash = useCallback(() => {
    if ((phase === 'winFlash' || phase === 'bustFlash')
        && performance.now() - flashAtRef.current > 700) toSettled();
  }, [phase, toSettled]);

  /* deep climbs walk off the ladder's edge — keep the lit rung in view */
  const ladderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ladderRef.current?.querySelector('.gm-rung--now')
      ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [picks, danger]);

  const maxStake = [...STEPS].reverse().find((s) => s <= balance) ?? STEPS[0];
  const cold = phase === 'set' || phase === 'settled';
  const inPlay = phase === 'arming' || phase === 'live' || phase === 'winFlash' || phase === 'bustFlash';
  const liveWait = phase === 'live' && picks === 0;
  const broke = balance < STEPS[0];

  /* the Status Strip: ONE sentence, never empty, never two */
  const strip = (() => {
    if (stripFx) return { cls: 'fx', text: stripFx };
    switch (phase) {
      case 'set': return { cls: '', text: 'SET BET, TAP START' };
      case 'arming': return { cls: 'arm', text: 'ROUND STARTING…' };
      case 'live': return liveWait
        ? { cls: 'live', text: 'TAP ANY SLAB' }
        : { cls: 'live blink', text: `TAP CASH OUT = ₹${cashAmt.toLocaleString('en-IN')}`, alt: 'OR DIG MORE' };
      case 'winFlash': return { cls: 'win', text: result?.auto ? `ALL GEMS! ₹${(result?.amount ?? 0).toLocaleString('en-IN')}` : `YOU WON ₹${(result?.amount ?? 0).toLocaleString('en-IN')}` };
      case 'bustFlash': return { cls: 'lose', text: `BOOM — LOST ₹${roundStakeRef.current.toLocaleString('en-IN')}` };
      case 'settled': return { cls: '', text: 'TAP REBET TO REPLAY' };
    }
  })();

  return (
    <div className="gm-root fixed inset-0 z-[9999]">
    <div ref={appRef} className={`gm-app gm-phase-${phase}`} onPointerDown={(e) => { unlockGmSound(); skipFlash(); void e; }}>
      <ArtDefs />

      {/* the ember frame — on only while money is in play */}
      <div className={`gm-frame ${phase === 'arming' || phase === 'live' ? 'on' : ''} ${phase === 'winFlash' ? 'win' : ''} ${phase === 'bustFlash' ? 'dead' : ''}`} aria-hidden />

      {/* ambience */}
      {motes.map((m, i) => (
        <span key={i} className="gm-mote"
              style={{ left: m.left, top: m.top, width: m.size, height: m.size,
                       ['--t' as string]: m.t, ['--d' as string]: m.d,
                       ['--dx' as string]: m.dx, ['--dy' as string]: m.dy }} />
      ))}

      {/* A. ribbon */}
      <div className="gm-head">
        <span className="gm-title">GEMS MINE</span>
        <span className="gm-shift">{`SHIFT ${shift}${phase === 'set' ? ' NEXT' : ''}`}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className={`gm-iconbtn ${soundOn ? '' : 'off'}`} aria-label="Sounds"
                  {...press(() => setSoundOn(setGmSoundOn(!isGmSoundOn())))}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
              <path d="M4 9v6h4l5 5V4L8 9H4z" /><path d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" />
            </svg>
          </button>
          <button type="button" className={`gm-iconbtn ${musicOn ? '' : 'off'}`} aria-label="Music"
                  {...press(() => setMusicOn(setGmMusicOn(!isGmMusicOn())))}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
              <path d="M9 17.2V6.3l10-2v10.9h-2V6.7l-6 1.2v9.3z" />
              <ellipse cx="7" cy="17.6" rx="2.9" ry="2.4" /><ellipse cx="17" cy="15.6" rx="2.9" ry="2.4" />
            </svg>
          </button>
          <span className={`gm-wallet ${walletFx}`} title="Practice chips">₹{balance.toLocaleString('en-IN')}</span>
        </div>
      </div>

      {/* B. the ladder (previews the selected risk at 55% while setting) */}
      <div className={`gm-ladder ${phase === 'set' ? 'preview' : ''}`} ref={ladderRef} aria-label="Multipliers">
        {rungs.slice(1, 16).map((m, i) => {
          const k = i + 1;
          const cls = k < picks ? 'gm-rung--done' : k === picks ? 'gm-rung--now' : k === picks + 1 ? 'gm-rung--next' : '';
          return <span key={`${bombs}-${k}`} className={`gm-rung ${cls}`}>{fmtX(m)}</span>;
        })}
      </div>

      {/* C. the wall, framed in mine timber, lit by the hero lanterns */}
      <div className={`gm-stage ${cold ? 'cold' : ''} ${phase === 'arming' ? 'ignite' : ''}`}>
        <div className="gm-wallwrap">
          <span className="gm-beamslot gm-beamslot--t"><TimberBeam /></span>
          <span className="gm-propslot gm-propslot--l"><TimberProp /></span>
          <span className="gm-propslot gm-propslot--r"><TimberProp /></span>
          <div className="gm-lantern-slot gm-lantern-slot--l"><Lantern size="calc(.19 * var(--gm-w))" /></div>
          <div className="gm-lantern-slot gm-lantern-slot--r"><Lantern size="calc(.19 * var(--gm-w))" flip /></div>
          <div className={`gm-grid ${shake ? 'shake' : ''}`}
               onPointerDown={() => { if (cold) { flashStrip('TAP START FIRST'); } }}>
            {tiles.map((t, i) => {
              const kind: GemKind = gemFor(seedRef.current, i);
              const open = t === 'gem' || t === 'bomb';
              return (
                <button key={i} type="button" disabled={phase !== 'live' || t !== 'covered'}
                        aria-label={`Tile ${i + 1}`}
                        className={`gm-tile ${open ? 'gm-tile--open' : ''} ${t === 'striking' ? 'gm-tile--striking' : ''} ${t === 'ghost' ? 'gm-tile--ghost' : ''}`}
                        {...press(() => strike(i))}>
                  <Socket />
                  {(t === 'gem' || (t === 'ghost' && !bombsRef.current.has(i))) && (
                    <span className="gm-gemslot">
                      {t === 'gem' && <RaysBurst tone="#FFE9A8" />}
                      <Gem kind={kind} size="100%" reveal={t === 'gem'} idle />
                    </span>
                  )}
                  {(t === 'bomb' || (t === 'ghost' && bombsRef.current.has(i))) && (
                    <span className="gm-bombslot">
                      <Bomb size="100%" lit={t === 'bomb'} />
                      {t === 'bomb' && <Explosion />}
                    </span>
                  )}
                  {(t === 'covered' || t === 'striking') && <TileFace seed={i * 7 + 3} />}
                  {(t === 'covered' || t === 'striking') && (i * 7 + 3) % 5 < 2 && <VeinOverlay />}
                  <CrackBurst />
                  <Shards />
                  <span className="gm-dustpuff" />
                  {t === 'striking' && <span className="gm-pickslot"><Pickaxe size="100%" /></span>}
                </button>
              );
            })}
          </div>
          <span className="gm-portalshade" aria-hidden />
          {phase === 'live' && (
            <span className="gm-embers" aria-hidden>
              {Array.from({ length: 6 }, (_, i) => (
                <i key={i} style={{ ['--x' as string]: `${12 + i * 15}%`, ['--d' as string]: `${i * 0.45}s` }} />
              ))}
            </span>
          )}
          {stamp && <span className="gm-stamp">{stamp.text}</span>}
          {hint && phase === 'live' && picks === 0 && (
            <span className="gm-hint" aria-hidden>👆 TAP A ROCK</span>
          )}
        </div>
      </div>

      {/* D. the status strip — the one sentence that always tells the truth */}
      <div className={`gm-strip ${strip.cls}`} key={strip.text}>
        <span className="gm-strip-main">{strip.text}</span>
        {'alt' in strip && strip.alt && <span className="gm-strip-alt">{strip.alt}</span>}
      </div>

      {/* E. THE DOCK — one machine, three forms */}
      <div className={`gm-dock gm-dock--${phase === 'set' ? 'set' : (phase === 'settled' ? 'deck' : 'lever')}`}
           onPointerDown={() => { if (draining) setDraining(false); }}>
        {phase === 'set' && (
          <div className="gm-form gm-form-set">
            <div className="gm-chiprail" role="radiogroup" aria-label="Stake">
              {CHIPS.map((c) => (
                <button key={c} type="button" data-v={c} disabled={c > balance}
                        aria-pressed={stake === c} aria-label={`₹${c} chip`}
                        className={`gm-chip ${stake === c ? 'sel' : ''}`}
                        {...press(() => { setStake(c); sTap(); })}>
                  {c >= 1000 ? `${c / 1000}k` : c}
                </button>
              ))}
            </div>
            <div className="gm-riskrow">
              {RISK_ORDER.map((d) => (
                <button key={d} type="button" className={`gm-risk gm-risk--${d} ${danger === d ? 'sel' : ''}`}
                        {...press(() => { setDanger(d); sTap(); })}>
                  <i>{'💣'.repeat(DANGERS[d].bombs)}</i>
                  <b>{RISK_LABEL[d]}</b>
                </button>
              ))}
            </div>
            <button type="button" className="gm-big gm-big--gold" disabled={stake > balance}
                    {...press(() => arm())}>
              {`START — ₹${stake.toLocaleString('en-IN')}`}
              <small>{stake > balance ? 'Not enough balance' : `${bombs} bomb${bombs > 1 ? 's' : ''} hidden`}</small>
            </button>
          </div>
        )}

        {(phase === 'arming' || phase === 'live' || phase === 'winFlash' || phase === 'bustFlash') && (
          <div className="gm-form gm-form-lever">
            <div className="gm-leverinfo">
              <span>{`Stake ₹${roundStakeRef.current.toLocaleString('en-IN')} · 💣×${liveBombs}`}</span>
              <button type="button" className={`gm-autopill ${auto ? 'on' : ''}`}
                      {...press(() => { const v = !auto; setAuto(v); lsSet('gm_auto', v ? '1' : '0'); sTap(); })}>
                {auto ? 'AUTO ON' : 'AUTO OFF'}
              </button>
            </div>
            <div className="gm-gembar"><i style={{ width: `${Math.round((picks / (TILES - liveBombs)) * 100)}%` }} /></div>
            <button type="button"
                    className={`gm-big gm-big--cash ${liveWait || phase === 'arming' ? 'wait' : ''} ${phase === 'winFlash' ? 'won' : ''} ${phase === 'bustFlash' ? 'lost' : ''}`}
                    disabled={phase !== 'live'}
                    {...press(() => cashout())}>
              {`CASH OUT ₹${cashAmt.toLocaleString('en-IN')}`}
              <small>{picks === 0 ? 'Your stake back' : `${fmtX(nowX)} · next ${fmtX(nextX)}`}</small>
            </button>
          </div>
        )}

        {phase === 'settled' && (
          <div className="gm-form gm-form-deck">
            <div className={`gm-resultline ${result?.won ? 'win' : 'lose'}`}>
              <em>{`SHIFT ${shift}`}</em>
              <b>{result?.won ? `You won ₹${result.amount.toLocaleString('en-IN')}` : `Lost ₹${roundStakeRef.current.toLocaleString('en-IN')}`}</b>
            </div>
            <button type="button" className="gm-big gm-big--gold gm-rebet"
                    {...press(() => {
                      if (broke) { window.location.href = '/wallet'; return; }
                      rebet();
                    })}>
              {broke ? 'ADD MONEY' : autoWait ? 'KEEP GOING?' : `REBET ₹${Math.min(stake, maxStake).toLocaleString('en-IN')}`}
              <small>{broke ? 'Balance is low' : stake > balance ? 'Balance is low' : `💣×${bombs} · same again`}</small>
              {draining && <span className="gm-drain" />}
            </button>
            <button type="button" className="gm-changebet" {...press(changeBet)}>CHANGE BET</button>
          </div>
        )}
      </div>

      {phase === 'winFlash' && (
        <span className="gm-coinfly" aria-hidden>
          {Array.from({ length: 5 }, (_, i) => (
            <i key={i} style={{ ['--d' as string]: `${i * 90}ms`, ['--sx' as string]: `${(i - 2) * 14}px` }} />
          ))}
        </span>
      )}

      {/* F. the believability feed — wallpaper goes last, sleeps while money plays */}
      <div className={`gm-ticker ${inPlay ? 'asleep' : ''}`} aria-hidden>
        <div className="gm-ticker-track">
          {[...ticker, ...ticker].map((t, i) => (
            <span key={i} className="gm-ticker-item">⛏️ <b>{t.split(' dug')[0]}</b>{t.slice(t.indexOf(' dug'))}</span>
          ))}
        </div>
      </div>
    </div>
    </div>
  );
};

export default GmPage;
